import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { can } from '@kronos/domain'
import type { Role } from '@kronos/domain'
import { repository } from '../data'
import type { BoardOrder } from '../data'
import { BuildOrderCard } from './BuildOrderCard'
import { NewBuildOrder } from './NewBuildOrder'
import { EmptyState, Panel, Section, StatTile } from './primitives'

/**
 * The Builds tab (§11.1) — **what the floor has been told to make.**
 *
 * Not a Kanban board, deliberately. Twenty-five units of one order are
 * twenty-five identical cards saying nothing, and dragging one from Blocked to
 * Ready would be a person asserting something about the shelf that the stock
 * ledger already knows. So a card is an *order*, and the columns a card would
 * have moved between are counts on it: ready, blocked, under way, built.
 *
 * What a builder needs from this screen is one number — how many can I start
 * right now — and one sentence when that number is zero: what we are waiting
 * on. Everything else on the card is context for those two.
 */
export function BuildsView({ roles }: { roles: readonly Role[] }): JSX.Element {
  const [placing, setPlacing] = useState(false)
  const mayPlan = can(roles, 'plan_demand')
  const orders = useQuery({ queryKey: ['build-orders'], queryFn: () => repository.buildOrders() })

  const rows = orders.data ?? []
  const live = rows.filter((o) => o.state === 'released' || o.state === 'draft')
  const done = rows.filter((o) => o.state === 'complete')

  return (
    <div className="page">
      {mayPlan ? (
        <div className="toolbar">
          <span className="toolbar__spacer" />
          <button className="primary" onClick={() => setPlacing(true)}>
            Order a build
          </button>
        </div>
      ) : null}

      {orders.isPending ? <p className="muted">Loading…</p> : null}
      {orders.isError ? <p className="critical">Could not load build orders.</p> : null}

      {orders.isSuccess && rows.length === 0 ? (
        <Panel>
          <EmptyState icon="builds" title="Nothing ordered yet">
            {mayPlan
              ? 'Order a build to put work on the floor. A draft is safe — nothing reaches the floor or the buy list until it’s released.'
              : 'Engineers order builds. Released ones show up here and on Today.'}
          </EmptyState>
        </Panel>
      ) : null}

      <Totals rows={rows.filter((o) => o.state === 'released')} />

      {live.map((order) => (
        <BuildOrderCard key={order.id} order={order} roles={roles} />
      ))}

      {done.length > 0 ? (
        <Section label="Complete">
          {done.map((order) => (
            <BuildOrderCard key={order.id} order={order} roles={roles} />
          ))}
        </Section>
      ) : null}

      {placing ? <NewBuildOrder onClose={() => setPlacing(false)} /> : null}
    </div>
  )
}

function Totals({ rows }: { rows: readonly BoardOrder[] }): JSX.Element | null {
  if (rows.length < 2) return null
  const sum = (pick: (o: BoardOrder) => number): number => rows.reduce((n, o) => n + pick(o), 0)
  const blocked = sum((o) => o.blocked)
  return (
    <div className="stat-tiles">
      <StatTile label="Ready to start" value={sum((o) => o.ready)} />
      <StatTile label="Waiting on parts" value={blocked} warning={blocked > 0} />
      <StatTile label="On the bench" value={sum((o) => o.inProgress)} />
      <StatTile label="Built" value={sum((o) => o.built)} />
    </div>
  )
}
